/**
 * AlertRenderer - Generates alert modal HTML
 *
 * @module AlertRenderer
 */

const ALERT_ICONS = {
    info   :'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 8V8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    warning:'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 9V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 17V17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M3.5 20.5L12 4L20.5 20.5H3.5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>',
    error  :'<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M15 9L9 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
};

/**
 * Alert Renderer Class
 */
export class AlertRenderer {
    /**
     * Create alert renderer
     * @param {Object} config - Configuration
     */
    constructor(config) {
        this.config = config;
    }

    /**
     * Generate alert modal HTML
     * @param {string} modalId - Modal ID
     * @param {string} message - Alert message
     * @param {Object} config - Alert configuration
     * @returns {string} HTML string
     */
    generateAlertHTML(modalId, message, config) {
        const type = config.type || 'info';
        const locales = config.locales || {};
        const alertClass = `bm-modal--alert-${type}`;
        const typeTitle = locales[type] || this.escapeHtml(type.charAt(0).toUpperCase() + type.slice(1));
        const titleText = config.title || typeTitle;
        const safeTitle = this.escapeHtml(titleText);
        const titleAttribute = config.showTitle === false
            ? `aria-label="${safeTitle}"`
            : `aria-labelledby="${modalId}-title"`;
        const iconsMap = config.icons || ALERT_ICONS;
        const iconContent = config.iconContent || iconsMap[type] || '';
        const iconHtml = config.showIcon !== false && iconContent
            ? `<div class="bm-alert__icon">${iconContent}</div>`
            : '';
        const closeLabel = locales.close || 'Close';
        const closeContent = config.closeButtonContent || '&times;';
        const closeButton = config.showCloseButton === false ? '' : `
            <button aria-label="${closeLabel}"
                    class="bm-modal__close"
                    data-basemodal-close>
                ${closeContent}
            </button>`;
        const titleHtml = config.showTitle === false ? '' : `
                <h2 id="${modalId}-title" class="bm-alert__title">${safeTitle}</h2>`;
        const overlay = config.showOverlay === false
            ? ''
            : '<div class="bm-modal__bg" aria-hidden="true" data-basemodal-close></div>';
        const actionsHtml = (config.showConfirmButton || config.showCancelButton) ? `
                <div class="bm-alert__actions">
                    ${config.showCancelButton ? `<button class="b-btn b-btn--secondary" data-alert-action="cancel">${config.cancelButtonContent || (locales.cancel || 'Cancel')}</button>` : ''}
                    ${config.showConfirmButton ? `<button class="b-btn b-btn--primary" data-alert-action="confirm">${config.confirmButtonContent || (locales.confirm || 'OK')}</button>` : ''}
                </div>` : '';

        return `
<div id="${modalId}" class="bm-modal bm-alert-modal ${alertClass}" aria-hidden="true" role="alertdialog" ${titleAttribute}>
    <div tabindex="-1" class="bm-modal__overlay">
        <div class="bm-modal__container bm-modal__container--alert" aria-modal="true">
            ${closeButton}

            ${iconHtml}

            <div class="bm-alert__content">
                ${titleHtml}
                <p class="bm-alert__message">${this.escapeHtml(message)}</p>
                ${actionsHtml}
            </div>
        </div>
        ${overlay}
    </div>
</div>`;
    }

    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string} Escaped HTML
     */
    escapeHtml(text) {
        const div       = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

export default AlertRenderer;
